import { createContext } from 'react'

import {
  background,
  border,
  coloring,
  composeGroups,
  flexContainer,
  flexItem,
  layout,
  margins,
  paddings,
  positioning,
  sizing,
  spacing,
  typography,
  type StyleGroup
} from './style'
import { createView, type KitViewProps } from './view'

/* -----------------------------------------------------------------------------
 * themed views
 *
 * The @walrus/common components were built as `themed(View)(...groups)` and
 * split props between their parts with `Part.styleKeys`. These keep that
 * vocabulary so their structure carries over as is.
 * -------------------------------------------------------------------------- */

export const positioningStyles = positioning
export const layoutStyles = layout
export const spacingStyles = spacing
export const marginStyles = margins
export const paddingStyles = paddings
export const sizingStyles = sizing
export const flexContainerStyles = flexContainer
export const flexItemStyles = flexItem
export const coloringStyles = coloring
export const typographyStyles = typography
export const borderStyles = border
export const backgroundStyles = background

export const displayStyle: StyleGroup = { display: layout.display }
export const widthStyle: StyleGroup = { width: sizing.width }
export const maxWidthStyle: StyleGroup = { maxWidth: sizing.maxWidth }
export const minWidthStyle: StyleGroup = { minWidth: sizing.minWidth }
export const backgroundColorStyle: StyleGroup = {
  backgroundColor: coloring.backgroundColor,
  bg: coloring.bg
}

// the input's `space` props: margins under another name (`s`, `sx`, `st`, `sr`...)
const spaceAlias = (from: string, to: string): StyleGroup => ({
  [from]: spacing[to]
})
export const spaceStyles: StyleGroup = Object.assign(
  {},
  spaceAlias('space', 'margin'),
  spaceAlias('s', 'margin'),
  spaceAlias('spaceX', 'marginX'),
  spaceAlias('sx', 'marginX'),
  spaceAlias('spaceY', 'marginY'),
  spaceAlias('sy', 'marginY'),
  spaceAlias('spaceTop', 'marginTop'),
  spaceAlias('st', 'marginTop'),
  spaceAlias('spaceBottom', 'marginBottom'),
  spaceAlias('sb', 'marginBottom'),
  spaceAlias('spaceLeft', 'marginLeft'),
  spaceAlias('sl', 'marginLeft'),
  spaceAlias('spaceRight', 'marginRight'),
  spaceAlias('sr', 'marginRight')
)

export type ThemedView = ReturnType<typeof createView> & {
  styleKeys: Set<string>
}

export const themedView = (
  displayName: string,
  ...groups: StyleGroup[]
): ThemedView => {
  const definitions = composeGroups(...groups)
  return Object.assign(createView({ displayName, groups: definitions }), {
    styleKeys: new Set(Object.keys(definitions))
  })
}

// splits `obj` into the keys `using` has and the rest
export const distribute = <T extends Record<string, any>>(
  obj: T,
  {
    using,
    to = {},
    rest = {}
  }: { using: Set<string>; to?: KitViewProps; rest?: KitViewProps }
) => {
  Object.keys(obj).forEach(key => {
    ;(using.has(key) ? to : rest)[key] = obj[key]
  })
  return [to, rest] as [KitViewProps, KitViewProps]
}

/* ---- theme variants ---------------------------------------------------------- */

export type ThemeVariants =
  | 'default'
  | 'lightPink'
  | 'green'
  | 'lightGreen'
  | 'violet'
  | 'gray'
  | 'punchThroughGray'
  | 'punchThroughPurple'
  | 'lightOrange'
  | 'paleViolet'
  | 'lightViolet'
  | 'walrusAI'

// which look fields and select buttons take inside a screen section; 'default'
// everywhere else, as @walrus/common's Root provided it
export const ThemeVariantContext = createContext<ThemeVariants>('default')
export const ThemeVariantProvider = ThemeVariantContext.Provider
